<template>
    <div>
        <div
            class="relative w-full items-center justify-between rounded-lg px-0 py-10 lg:px-22 lg:py-10"
        >
            <input
                id="searchUlicnik"
                ref="searchbar"
                v-model="input"
                type="text"
                class="w-full border bg-white px-3 py-2 sm:px-4 sm:py-3"
                placeholder="Název ulice"
                autocomplete="off"
                @keyup="onSearch"
                @keydown.enter="results.length > 0 && searchResults()"
                @keyup.escape="areSuggestionsVisible = false"
                @keydown.down.prevent="focusElWithIdx(0)"
                @keydown.tab.prevent="focusElWithIdx(0)"
                @keydown.up.prevent="focusElWithIdx(results.length - 1)"
            />
            <ul
                v-show="areSuggestionsVisible && results.length > 0"
                id="suggestions"
                ref="suggestions"
                class="flex max-h-60 w-full flex-col overflow-y-auto bg-white font-[SpaceGrotesk] text-[14px] whitespace-pre-line sm:text-[16px]"
                :class="{ active: store.suggestionsActive }"
            >
                <li
                    v-for="(result, idx) in results"
                    :id="'suggestionsItem' + idx"
                    :key="idx"
                    tabindex="0"
                    class="p-2 focus:cursor-pointer focus:bg-[#FDF0F0] focus:outline-none"
                    @click="selectStreet(result)"
                    @keyup.enter.prevent="selectStreet(result)"
                    @keydown.up.prevent="focusElWithIdx(idx - 1)"
                    @keydown.down.prevent="focusElWithIdx(idx + 1)"
                >
                    <SearchRow
                        :query="eventTargetValue"
                        :result="result"
                    />
                </li>
            </ul>

            <div class="mt-4 flex justify-center">
                <button
                    class="cursor-pointer rounded-full bg-[#F01F26] px-8 py-2 text-white hover:bg-[#D01A22] focus:outline-none"
                    @click="searchResults"
                >
                    <p class="source font-bold">Hledat</p>
                </button>
            </div>

            <!-- Výsledky hledání -->
            <div
                id="results-hledat"
                class="font-[SpaceGrotesk]"
            >
                <p
                    v-if="store.resultSuggestions.length"
                    class="mb-5 font-bold sm:mb-10"
                >
                    Pro heslo
                    <b class="text-[#F01F26]">
                        {{ store.eventTargetValueHledat }}
                    </b>

                    <span v-if="store.resultSuggestions.length > 1">
                        bylo nalezeno</span
                    >
                    <span v-else> byl nalezen</span>

                    {{ store.resultSuggestions.length }}

                    <span v-if="store.resultSuggestions.length > 1"
                        >výsledků.</span
                    >
                    <span v-else>výsledek.</span>
                </p>
                <ul
                    v-if="store.resultSuggestions.length"
                    class="flex w-full flex-col overflow-y-auto text-[14px] whitespace-pre-line sm:text-[16px]"
                >
                    <li
                        v-for="(resultHledat, index) in store.resultSuggestions"
                        :key="index"
                        class="cursor-pointer border-b-2 border-gray-200 py-2 hover:bg-[#FDF0F0]"
                        @click="selectStreet(resultHledat)"
                    >
                        <SearchRow
                            :query="store.eventTargetValueHledat"
                            :result="resultHledat"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useRouter } from "vue-router"
import debounce from "lodash/debounce"

const router = useRouter()

const store = useMainStore()

const { serverUri } = useDataLogic()

const selected = ref("nazev-ulice")

const eventTargetValue = ref("")

interface Street {
    "nazev-ulice": string
    "typ-hesla-ulice"?: string
    katastr?: string
}

const results = ref<Street[]>([])

const searchbar = ref<HTMLElement | null>(null)
const suggestions = ref<HTMLElement | null>(null)

function findCorrectCoordinates() {
    if (searchbar.value && suggestions.value) {
        const searchbarRect = searchbar.value.getBoundingClientRect()

        suggestions.value.style.top = `${searchbarRect.bottom}px`
        suggestions.value.style.left = `${searchbarRect.left}px`
    }
}

const input = ref("")
const areSuggestionsVisible = ref(false)
const isSearching = ref(false)

watch(selected, _selectedSearch => {
    const searchInput = document.getElementById(
        "searchUlicnik"
    ) as HTMLInputElement
    if (searchInput) {
        searchInput.value = ""
        results.value = []
    }
})

const onSearch = debounce(async function (event: any) {
    if (selected.value === "nazev-ulice") {
        searchNazevUlice(event)
    }
}, 300)

function searchResults() {
    store.suggestionsActive = true

    router.push({ name: "vyhledavani" })

    if (eventTargetValue.value.length < 3) {
        alert("Zadejte alespoň 3 znaky pro vyhledávání.")
        return
    }

    if (results.value.length === 0) {
        alert("Nebyly nalezeny žádné výsledky.")
        return
    }

    store.eventTargetValueHledat = eventTargetValue.value

    store.resultSuggestions = results.value
}

store.suggestionsActive = false
async function searchNazevUlice(event: any) {
    input.value = event.target.value

    if (input.value.length > 2) {
        const nepovolenyZnak = input.value.search(
            /(\[|\]|\(|\)|\{|\}|\||\^|\$|\*|\+|\?|\\)/
        )

        if (nepovolenyZnak >= 0) {
            alert(
                "Zadaný text obsahuje některý z nepovolených znaků: [, ], (, ), {, }, |, ^, $, *, +, ?, \\]"
            )
            return
        } else {
            eventTargetValue.value = input.value

            const responseData = await $fetch<Street[]>("select-text", {
                baseURL: serverUri(),
                method: "post",
                body: {
                    "hledany-text": input.value,
                },
            })

            if (responseData) {
                results.value = responseData.result
            } else {
                results.value = []
            }
        }
    } else {
        results.value = []
    }
}

function selectStreet(result: any) {
    router.push({ name: "detail", query: { id: result.id } })
    results.value = []
}

function focusElWithIdx(index: number) {
    if (index < 0 || index >= results.value.length) {
        const inputEl = document.querySelector("#searchUlicnik") as HTMLElement
        if (inputEl) {
            inputEl.focus()
        }
    } else {
        const element = document.querySelector(
            `#suggestionsItem${index}`
        ) as HTMLElement
        if (element) {
            element.focus()
        }
    }
}

watch(input, () => {
    if (input.value.length > 1 && isSearching.value === false) {
        findCorrectCoordinates()
        areSuggestionsVisible.value = true
    } else {
        areSuggestionsVisible.value = false
    }
})

onClickOutside(searchbar, () => {
    areSuggestionsVisible.value = false
})
</script>

<style lang="scss"></style>
